import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Loading, Avatar, StreakBadge } from "../components/common";
import { Plus, Trophy, Target, ChevronRight, PlayCircle } from "lucide-react";

export default function Dashboard() {
  const [players, setPlayers] = useState(null);
  const [inProgress, setInProgress] = useState([]);
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [p, prog, done] = await Promise.all([
          api.get("/players"),
          api.get("/matches", { params: { status: "in_progress" } }),
          api.get("/matches", { params: { status: "completed" } }),
        ]);
        setPlayers(p.data);
        setInProgress(prog.data);
        setRecent(done.data.slice(0, 5));
      } catch (e) {
        if (e.response?.status !== 401) toast.error("Could not load dashboard. Please retry.");
        setPlayers([]);
      }
    })();
  }, []);

  if (players === null) return <Loading label="Loading dashboard" />;

  const totalMatches = recent.length;

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">
            Match Control Room
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none">
            Home Oche
          </h1>
        </div>
        <button
          data-testid="dashboard-new-match-btn"
          onClick={() => navigate("/new")}
          className="flex items-center gap-2 px-5 py-3 bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold uppercase tracking-wide rounded-sm transition-colors duration-150 self-start"
        >
          <Plus className="w-5 h-5" strokeWidth={3} /> Start New Match
        </button>
      </div>

      {inProgress.length > 0 && (
        <div>
          <SectionTitle icon={PlayCircle} accent="#FFCC00">
            Resume In Progress
          </SectionTitle>
          <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
            {inProgress.map((m) => (
              <button
                key={m.id}
                data-testid={`resume-match-${m.id}`}
                onClick={() => navigate(`/match/${m.id}`)}
                className="panel rounded-sm p-4 text-left hover:border-[#FFCC00] transition-colors duration-150 border-l-2 border-l-[#FFCC00]"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wide text-[#FFCC00]">
                    {gameLabel(m.game_type)} • Best of {m.best_of}
                  </span>
                  <PlayCircle className="w-4 h-4 text-[#FFCC00]" />
                </div>
                <div className="font-display text-xl font-bold text-white mt-1 truncate">
                  {Object.values(m.player_names).join("  vs  ")}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionTitle icon={Trophy}>Players</SectionTitle>
          {players.length === 0 ? (
            <EmptyPlayers onAdd={() => navigate("/players")} />
          ) : (
            <div className="grid gap-1 sm:grid-cols-2">
              {players.map((p) => (
                <button
                  key={p.id}
                  data-testid={`dashboard-player-${p.id}`}
                  onClick={() => navigate(`/players/${p.id}`)}
                  className="panel rounded-sm p-4 flex items-center gap-3 text-left hover:border-[#007AFF] transition-colors duration-150"
                >
                  <Avatar name={p.name} size={44} />
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-xl font-bold text-white truncate">
                      {p.name}
                    </div>
                    <div className="text-sm text-slate-400 font-semibold">
                      {p.overall.matches_won}-{p.overall.matches_lost}
                      <span className="text-slate-600 mx-1.5">·</span>
                      {p.overall.win_pct}% WR
                      <span className="text-slate-600 mx-1.5">·</span>
                      <StreakBadge streak={p.overall.current_streak} className="text-sm" />
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-600" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <SectionTitle icon={Target}>Recent Matches</SectionTitle>
          {totalMatches === 0 ? (
            <div className="panel rounded-sm p-6 text-slate-500 text-sm">
              No completed matches yet. Play one to build the record book.
            </div>
          ) : (
            <div className="space-y-1">
              {recent.map((m) => (
                <button
                  key={m.id}
                  data-testid={`recent-match-${m.id}`}
                  onClick={() => navigate(`/match/${m.id}`)}
                  className="panel rounded-sm p-3 w-full text-left hover:border-[#007AFF] transition-colors duration-150"
                >
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    {gameLabel(m.game_type)} • Bo{m.best_of}
                  </div>
                  <div className="font-semibold text-white text-sm mt-0.5">
                    {m.player_names[m.winner_id]}{" "}
                    <span className="text-[#FFCC00]">won</span>{" "}
                    {scoreString(m)}
                  </div>
                </button>
              ))}
              <button
                onClick={() => navigate("/history")}
                className="text-sm text-[#007AFF] font-semibold px-3 py-2 hover:underline"
              >
                View all history →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function scoreString(m) {
  const fs = m.final_score || {};
  return m.player_ids.map((id) => fs[id] ?? 0).join(" - ");
}

export function gameLabel(g) {
  if (g === "501") return "501";
  if (g === "baseball") return "Baseball";
  return "Around the World";
}

function SectionTitle({ icon: Icon, children, accent }) {
  return (
    <h2 className="flex items-center gap-2 font-display text-2xl font-bold uppercase tracking-tight text-white mb-3">
      <Icon className="w-5 h-5" style={{ color: accent || "#007AFF" }} />
      {children}
    </h2>
  );
}

function EmptyPlayers({ onAdd }) {
  return (
    <div className="panel rounded-sm p-8 text-center">
      <p className="text-slate-400 mb-4">No players yet. Add players to start tracking.</p>
      <button
        data-testid="dashboard-add-players-btn"
        onClick={onAdd}
        className="px-5 py-2.5 bg-[#007AFF] text-white font-bold uppercase tracking-wide rounded-sm"
      >
        Manage Players
      </button>
    </div>
  );
}
